import { formatPence, formatPrice, formatUnitPence, shortDate } from './money';

/**
 * Pure rules for the pricing screens: meters, who sees which button, Bailey's
 * free-question counter and the light alerts. No React, no fetching, so the
 * page, the Bailey chat, the alert bar and the tests all read the same thing.
 *
 * Everything tolerates a missing block or field: a backend that hasn't sent
 * something yet hides that part instead of breaking the page.
 */

export const WARN_PCT = 80;
export const FULL_PCT = 100;
export const BAILEY_COUNTER_FROM_PCT = 50;

// What the receptionist includes, ONLY for when the API hasn't said (it
// should: prices.upgradeMinutes / upgradeReplies). Prices are never taken
// from here. (Pricing 2026-10-07: Bailey Plus and the AI receptionist are
// two add-ons; the receptionist carries the minutes, replies and number.)
export const UPGRADE_INCLUDES_FALLBACK = {
    solo: { minutes: 100, replies: 250, numbers: 1 },
    salon: { minutes: 200, replies: 500, numbers: 1 },
};
export const FREE_BAILEY_QUESTIONS_FALLBACK = 30;
export const BAILEY_PLUS = 'Bailey Plus';
// The owner's monthly limit on receptionist use past the allowance.
export const CAP_OPTIONS_PENCE = [0, 1000, 2500, 5000, 10000];

const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const plural = (n, one, many) => `${Number(n).toLocaleString('en-GB')} ${n === 1 ? one : many}`;

/**
 * A usage meter.
 *   allowance null/undefined → unlimited (no bar, level 'ok')
 *   level: 'ok' | 'warn' (80%+) | 'full' (100%+)
 */
export function meter(used, allowance) {
    const u = Math.max(0, num(used) || 0);
    const a = num(allowance);
    if (a == null) return { used: u, allowance: null, unlimited: true, pct: 0, level: 'ok', left: null };
    const pct = a <= 0 ? (u > 0 ? 100 : 0) : Math.min(100, Math.round((u / a) * 100));
    const rawPct = a <= 0 ? (u > 0 ? 100 : 0) : (u / a) * 100;
    const level = rawPct >= FULL_PCT ? 'full' : rawPct >= WARN_PCT ? 'warn' : 'ok';
    return { used: u, allowance: a, unlimited: false, pct, level, left: Math.max(0, a - u) };
}

/** The text a screen reader hears for a meter: "12 of 30 questions used". */
export function meterText(m, unitPlural) {
    if (!m) return '';
    if (m.unlimited) return `${m.used.toLocaleString('en-GB')} ${unitPlural} used. Unlimited.`;
    return `${m.used.toLocaleString('en-GB')} of ${m.allowance.toLocaleString('en-GB')} ${unitPlural} used`;
}

export const isSolo = (usage) => !!(usage && usage.plan && usage.plan.type === 'solo');
/** A self-employed stylist looking at their own Plan & billing (never the salon's). */
export const isSelfEmployedView = (usage) => !!(usage && (usage.viewer === 'self-employed' || (usage.plan && usage.plan.type === 'self-employed-in-salon')));
/** Who is looking: 'owner' | 'staff' | 'self-employed' (older APIs: owner). */
export const viewerOf = (usage) => (isSelfEmployedView(usage) ? 'self-employed' : (usage && usage.viewer) || 'owner');
/** Priced per location: a salon plan, not Solo or a self-employed stylist's own. */
const perLocation = (usage) => !!(usage && usage.plan && usage.plan.type === 'salon');
/** Card on file? needsCard: something (reminders, a top-up, the upgrade) wants one. */
export function cardState(usage) {
    const c = (usage && usage.card) || {};
    const b = (usage && usage.balance) || {};
    const pick = (k) => (c[k] != null ? c[k] : b[k]);
    return { cardOnFile: pick('cardOnFile') == null ? null : !!pick('cardOnFile'), needsCard: !!pick('needsCard') };
}
export const isUpgraded = (usage) => !!(usage && usage.plan && usage.plan.baileyUpgrade && usage.plan.baileyUpgrade.active);
/** Existing salons kept on the 2026 prices (6p texts, 4p reminders over). */
export const isLegacyPricing = (usage) => !!(usage && usage.plan && usage.plan.pricingVersion === '2026');

/**
 * What Bailey Plus costs THIS business a month, in pence: what the plan says
 * it pays if it has it, else the price for its type (Solo, or per location).
 * null when the API hasn't said.
 */
export function upgradePricePence(usage) {
    if (!usage) return null;
    const plan = usage.plan || {};
    const up = plan.baileyUpgrade || {};
    if (up.active && num(up.pricePence) != null) return num(up.pricePence);
    const prices = usage.prices || {};
    if (isSelfEmployedView(usage) && num(up.pricePence) != null) return num(up.pricePence);
    const byType = isSolo(usage) ? num(prices.upgradeSoloPence) : num(prices.upgradeLocationPence);
    if (byType != null) return byType;
    return num(up.pricePence);
}

/** "+£15 a month" / "+£29 a month per location". '' when unknown. */
export function upgradePriceLine(usage) {
    const p = upgradePricePence(usage);
    if (p == null) return '';
    return `+${formatPrice(p)} a month${perLocation(usage) ? ' per location' : ''}`;
}

/**
 * What starting Bailey Plus charges, in one sentence, from the plan's
 * baileyUpgrade { billing, chargeNow, trialEndsAt, prorationPence }:
 *   chargeNow false            → nothing until the trial ends
 *   same_subscription + now    → the part-month amount today, then monthly
 *   separate_monthly (yearly)  → monthly on the card from today
 * '' when the API hasn't said enough: callers keep their general copy.
 * Never shows a part-month amount when there is no charge now.
 */
export function upgradeChargeLine(usage, now) {
    if (!usage || !usage.plan) return '';
    const up = usage.plan.baileyUpgrade || {};
    const price = upgradePricePence(usage);
    if (price == null) return '';
    const monthly = `+${formatPrice(price)} a month`;
    if (up.chargeNow === false) {
        const when = shortDate(up.trialEndsAt, now);
        return when
            ? `No charge until your free trial ends on ${when}. Then ${monthly}.`
            : `No charge until your free trial ends. Then ${monthly}.`;
    }
    if (up.billing === 'separate_monthly' && isSelfEmployedView(usage)) {
        return `${monthly}, billed monthly on your own card from today.`;
    }
    if (up.billing === 'separate_monthly') {
        return `${monthly}, billed monthly on your card from today. Your yearly plan isn’t changed.`;
    }
    const pro = num(up.prorationPence != null ? up.prorationPence : (usage.prices || {}).prorationPence);
    if (up.chargeNow === true && up.billing === 'same_subscription' && pro != null) {
        return `${formatPence(pro)} today for the rest of this billing month, then ${monthly}.`;
    }
    return '';
}

/**
 * The upgrade POST failed: what to say, and whether to point at Billing.
 *   payment_method_required → add a card first (link to Billing)
 *   yearly_plan_upgrade_unavailable → kept as a harmless fallback message
 *   anything else → a friendly retry
 */
export function upgradeErrorCopy(err) {
    const code = err && (err.code || (err.data && err.data.code));
    if (code === 'payment_method_required') return { text: 'Add a card first, then upgrade.', billing: true };
    if (code === 'se_upgrade_unavailable') return { text: 'Bailey Plus for self-employed stylists is coming soon. Nothing has been charged.', billing: false };
    if (code === 'no_subscription') return { text: 'Nothing has been charged. Add-ons go on your monthly Beu plan, and this account isn’t billed through Beu yet. Get in touch and we’ll set it up.', billing: false };
    if (code === 'yearly_plan_upgrade_unavailable') return { text: 'This can’t be added to your plan from here just now. Get in touch and we’ll sort it.', billing: false };
    if (code === 'trial_used') return { text: 'Your business has already had its free trial.', billing: false };
    return { text: 'That didn’t go through. Nothing has been charged. Try again in a moment.', billing: false };
}

/** Bailey Plus's monthly question cap for this business (500 one-person, 1,000 a location). */
export function baileyPlusCap(usage) {
    const up = (usage && usage.plan && usage.plan.baileyUpgrade) || {};
    if (num(up.cap) != null) return num(up.cap);
    const b = (usage && usage.bailey) || {};
    if (num(b.plusCap) != null) return num(b.plusCap);
    const p = (usage && usage.prices) || {};
    return isSolo(usage) || isSelfEmployedView(usage) ? (num(p.baileyPlusCapSolo) || 500) : (num(p.baileyPlusCapLocation) || 1000);
}

/** What the AI receptionist includes for this business type (minutes, replies, numbers). */
export function upgradeIncludes(usage) {
    if (isSelfEmployedView(usage)) {
        // Their own upgrade: unlimited Bailey, no receptionist, no number.
        const inc = (usage.plan.baileyUpgrade && usage.plan.baileyUpgrade.includes) || {};
        return { minutes: 0, replies: 0, numbers: num(inc.numbers) || 0, receptionist: !!inc.receptionist, selfEmployed: true };
    }
    const solo = isSolo(usage);
    const fb = UPGRADE_INCLUDES_FALLBACK[solo ? 'solo' : 'salon'];
    const prices = (usage && usage.prices) || {};
    const rec = (usage && usage.receptionist) || {};
    const upgraded = isUpgraded(usage);
    const on = upgraded || !!rec.active;
    const minutes = num(prices.upgradeMinutes) != null ? num(prices.upgradeMinutes)
        : on && rec.minutes && num(rec.minutes.allowance) ? num(rec.minutes.allowance) : fb.minutes;
    const replies = num(prices.upgradeReplies) != null ? num(prices.upgradeReplies)
        : on && rec.replies && num(rec.replies.allowance) ? num(rec.replies.allowance) : fb.replies;
    const numbers = num(prices.upgradeNumbers) != null ? num(prices.upgradeNumbers)
        : usage && usage.numbers && num(usage.numbers.included) && on ? num(usage.numbers.included) : fb.numbers;
    return { minutes, replies, numbers, receptionist: true, selfEmployed: false };
}

/**
 * The plan in words.
 *   name:  'Solo' | 'Salon & Barbershop'
 *   price: '£25 a month' | '£59 a month per location'
 *   seats: '3 self-employed stylists · £5 each' | ''
 */
export function planSummary(usage) {
    const plan = (usage && usage.plan) || {};
    if (isSelfEmployedView(usage)) {
        return { name: 'Self-employed', price: '', seats: '', seatsTotalPence: null, legacy: plan.pricingVersion === '2026', selfEmployed: true };
    }
    const solo = plan.type === 'solo';
    const base = num(plan.basePricePence);
    const seats = num(plan.seSeats) || 0;
    const seatPrice = num(plan.sePricePence);
    return {
        name: solo ? 'Solo' : 'Salon & Barbershop',
        price: base == null ? '' : `${formatPrice(base)} a month${solo ? '' : ' per location'}`,
        seats: !solo && seats > 0
            ? `${plural(seats, 'self-employed stylist', 'self-employed stylists')}${seatPrice != null ? ` · ${formatPrice(seatPrice)} each` : ''}`
            : '',
        seatsTotalPence: !solo && seatPrice != null ? seats * seatPrice : null,
        legacy: plan.pricingVersion === '2026',
    };
}

/**
 * Which plan buttons someone sees.
 *   canUpgrade  owner, not upgraded         → "Upgrade to Bailey Plus"
 *   canManage   owner, upgraded             → "Manage" (cancel inside)
 *   askOwner    not the owner, not upgraded → "Ask the owner" note, no button
 *   noPlan      may upgrade, but no plan billed through Beu to add it to
 *               (API baileyUpgrade.available === false) → "get in touch", no button
 * Nobody but the owner changes what the business pays.
 */
export const NO_PLAN_COPY = 'Add-ons go on your monthly Beu plan, and this account isn’t billed through Beu yet. Get in touch and we’ll set it up.'

/** Why an add-on can't be started here (its unavailableReason). */
export function unavailableCopy(usage, which = 'baileyUpgrade') {
    const reason = usage && usage.plan && usage.plan[which] && usage.plan[which].unavailableReason;
    const name = which === 'receptionistAddon' ? 'The AI receptionist' : BAILEY_PLUS;
    if (reason === 'yearly_plan') return `${name} on yearly plans is coming soon. Get in touch and we’ll set it up for you.`;
    if (reason === 'coming_soon') return `${name} for self-employed stylists is coming soon.`;
    return NO_PLAN_COPY;
}

export function ctaState(usage, { isOwner } = {}) {
    if (!usage || !usage.plan) return { canUpgrade: false, canManage: false, askOwner: false, upgraded: false, noPlan: false };
    const upgraded = isUpgraded(usage);
    // A self-employed stylist changes their OWN upgrade; staff change nothing.
    const viewer = viewerOf(usage);
    const may = viewer === 'self-employed' || (viewer !== 'staff' && !!isOwner);
    const up = usage.plan.baileyUpgrade || {};
    const noPlan = may && !upgraded && up.available === false;
    return {
        upgraded,
        canUpgrade: may && !upgraded && !noPlan,
        canManage: may && upgraded,
        askOwner: !may && !upgraded,
        noPlan,
    };
}

/**
 * Text and email prices for the "how pricing works" copy and cost lines.
 * Straight from the API: a salon on 2026 pricing sees 6p, a new one 8p.
 */
export function priceList(usage) {
    const p = (usage && usage.prices) || {};
    const rem = (usage && usage.reminders) || {};
    return {
        text: num(p.textPence) != null ? formatUnitPence(p.textPence) : '',
        email: num(p.emailPence) != null ? formatUnitPence(p.emailPence) : '',
        reminderOver: num(rem.overagePricePence) != null ? formatUnitPence(rem.overagePricePence) : '',
        pack: num(p.packPence) != null ? formatPrice(p.packPence) : '',
        packMinutes: num(p.packMinutes),
        packReplies: num(p.packReplies),
    };
}

/** "£10 for 25 minutes and 50 replies". '' when the API hasn't said. */
export function packLine(usage) {
    const l = priceList(usage);
    if (!l.pack) return '';
    const parts = [];
    if (l.packMinutes != null) parts.push(plural(l.packMinutes, 'minute', 'minutes'));
    if (l.packReplies != null) parts.push(plural(l.packReplies, 'reply', 'replies'));
    return parts.length ? `${l.pack} for ${parts.join(' and ')}` : l.pack;
}

/**
 * The AI receptionist add-on, in one object (pricing 2026-10-07).
 *   state: 'on' | 'trial' | 'trial-ended' | 'off' | 'legacy'
 *   pricePence: what it costs on its own; stepPence: what turning it on adds
 *   now (less when Bailey Plus is on: the two become the bundle)
 *   trial: { available, usedElsewhere, endsAt, minutes, replies, days }
 *   overage: { minutePence, replyPence, capPence, chargedPence, capReached }
 */
export function receptionistState(usage) {
    const r = (usage && usage.receptionist) || {};
    const add = (usage && usage.plan && usage.plan.receptionistAddon) || {};
    const t = r.trial || {};
    const p = (usage && usage.prices) || {};
    const solo = isSolo(usage);
    const active = !!(add.active || r.active);
    let state = 'off';
    if (active) state = 'on';
    else if (t.active || r.mode === 'trial') state = 'trial';
    else if (t.ended) state = 'trial-ended';
    else if (r.mode === 'legacy') state = 'legacy';
    const pricePence = num(add.pricePence) != null ? num(add.pricePence) : num(solo ? p.receptionistSoloPence : p.receptionistLocationPence);
    const o = r.overage || {};
    return {
        state,
        active,
        pricePence,
        stepPence: num(add.stepPence) != null ? num(add.stepPence) : pricePence,
        perLocation: perLocation(usage),
        available: add.available !== false,
        unavailableReason: add.unavailableReason || null,
        trial: {
            available: !!t.available && !active,
            usedElsewhere: !!t.usedElsewhere,
            endsAt: t.endsAt || null,
            days: num(t.days) || num(p.trialDays) || 14,
            minutes: meter(t.minutes && t.minutes.used, t.minutes && t.minutes.allowance != null ? t.minutes.allowance : num(p.trialMinutes)),
            replies: meter(t.replies && t.replies.used, t.replies && t.replies.allowance != null ? t.replies.allowance : num(p.trialReplies)),
        },
        overage: {
            minutePence: num(o.minutePence) != null ? num(o.minutePence) : num(p.overageMinutePence),
            replyPence: num(o.replyPence) != null ? num(o.replyPence) : num(p.overageReplyPence),
            capPence: num(o.capPence) != null ? num(o.capPence) : num(p.defaultCapPence),
            chargedPence: num(o.chargedPence) || 0,
            capReached: !!o.capReached,
        },
    };
}

/** "+£29 a month per location" for the receptionist; with Bailey Plus on, the step to the bundle. */
export function receptionistPriceLine(usage, { step = false } = {}) {
    const s = receptionistState(usage);
    const p = step ? s.stepPence : s.pricePence;
    if (p == null) return '';
    return `+${formatPrice(p)} a month${s.perLocation ? ' per location' : ''}`;
}

/**
 * What adding the receptionist charges, in one sentence (its own billing
 * facts: plan.receptionistAddon { chargeNow, trialEndsAt, prorationPence,
 * billing }). Uses the STEP price (with Bailey Plus on, the bundle's extra).
 */
export function receptionistChargeLine(usage, now) {
    const add = (usage && usage.plan && usage.plan.receptionistAddon) || {};
    const s = receptionistState(usage);
    if (s.stepPence == null) return '';
    const monthly = `+${formatPrice(s.stepPence)} a month`;
    if (add.chargeNow === false) {
        const when = shortDate(add.trialEndsAt, now);
        return `No charge until your free trial ends${when ? ` on ${when}` : ''}. Then ${monthly}.`;
    }
    if (add.billing === 'separate_monthly') return `${monthly}, billed monthly on your card from today. Your yearly plan isn’t changed.`;
    const pro = num(add.prorationPence);
    if (add.chargeNow === true && add.billing === 'same_subscription' && pro != null) {
        return `${formatPence(pro)} today for the rest of this billing month, then ${monthly}.`;
    }
    return `${monthly}.`;
}

/** "Past your allowance: 30p a minute and 2p a reply, up to £25 a month." */
export function overageLine(usage) {
    const o = receptionistState(usage).overage;
    if (o.minutePence == null || o.replyPence == null) return '';
    if (o.capPence === 0) return 'When the allowance is used, it takes messages until the 1st.';
    return `Past the allowance: ${formatUnitPence(o.minutePence)} a minute and ${formatUnitPence(o.replyPence)} a reply from your balance${o.capPence != null ? `, up to ${formatPrice(o.capPence)} a month` : ''}. Then it takes messages.`;
}

/** Words for one limit option: £0 → "No extra (stop at the allowance)". */
export function capOptionLabel(pence) {
    return Number(pence) === 0 ? 'No extra: stop at the allowance' : `Up to ${formatPrice(pence)} a month`;
}

/**
 * Receptionist meters: the month's allowance plus anything packs added.
 * (Contract: `allowance` is the plan's; `packs.minutesAdded` sits on top.)
 */
export function receptionistMeters(usage) {
    const r = (usage && usage.receptionist) || {};
    const packs = r.packs || {};
    const mins = r.minutes || {};
    const reps = r.replies || {};
    const total = (a, added) => (num(a) == null ? null : num(a) + (num(added) || 0));
    return {
        minutes: meter(mins.used, total(mins.allowance, packs.minutesAdded)),
        replies: meter(reps.used, total(reps.allowance, packs.repliesAdded)),
        packs: num(packs.count) || 0,
        autoPacks: !!r.autoPacks,
        alertLevel: num(r.alertLevel) || 0,
    };
}

/**
 * Bailey's free questions.
 *   showCounter  from 50% used
 *   nudge        from 80% used
 *   exhausted    none left
 * Unlimited (allowance null) never counts.
 */
export function baileyQuota(bailey) {
    const b = bailey || {};
    if (b.allowance == null) {
        // Bailey Plus: a monthly cap (500 / 1,000). Counted only near the top.
        const cap = num(b.cap);
        if (cap != null) {
            const m = meter(b.used, cap);
            const rawPct = cap > 0 ? (m.used / cap) * 100 : 100;
            return {
                unlimited: false, plus: true, used: m.used, allowance: null, cap, left: m.left, pct: m.pct,
                showCounter: rawPct >= WARN_PCT, nudge: false, exhausted: m.used >= cap,
                label: `${Math.min(m.used, cap).toLocaleString('en-GB')} of ${cap.toLocaleString('en-GB')} questions this month`,
                resetsAt: b.resetsAt || null,
            };
        }
        return { unlimited: true, used: num(b.used) || 0, allowance: null, showCounter: false, nudge: false, exhausted: false, label: '', resetsAt: b.resetsAt || null };
    }
    const m = meter(b.used, b.allowance);
    const rawPct = m.allowance > 0 ? (m.used / m.allowance) * 100 : 100;
    return {
        unlimited: false,
        used: m.used,
        allowance: m.allowance,
        left: m.left,
        pct: m.pct,
        showCounter: rawPct >= BAILEY_COUNTER_FROM_PCT,
        nudge: rawPct >= WARN_PCT,
        exhausted: m.used >= m.allowance,
        label: `${Math.min(m.used, m.allowance).toLocaleString('en-GB')} of ${m.allowance.toLocaleString('en-GB')} free questions this month`,
        resetsAt: b.resetsAt || null,
    };
}

/**
 * Is this chat error Bailey saying the free questions are used up? The agent
 * endpoint refuses with data { code: 'bailey_quota', used, allowance,
 * resetsAt, upgrade: { pricePence } }; accept it on the event, under `data`,
 * or under `error.data`, however the transport wraps it.
 */
export const QUOTA_CODES = ['bailey_quota', 'bailey_cap', 'bailey_staff_access'];
export function quotaRefusal(ev) {
    if (!ev) return null;
    // The stream's event is { type, message, code, data }: the figures live in
    // `data`, so look there before the event itself.
    const cands = [ev.data, ev.error && ev.error.data, ev, ev.error, ev.responseData];
    const hit = cands.find((c) => c && typeof c === 'object' && QUOTA_CODES.includes(c.code));
    if (!hit) return null;
    return {
        code: hit.code,
        cap: num(hit.cap),
        reason: hit.reason || null,
        used: num(hit.used),
        allowance: num(hit.allowance) != null ? num(hit.allowance) : FREE_BAILEY_QUESTIONS_FALLBACK,
        resetsAt: hit.resetsAt || null,
        upgradePricePence: hit.upgrade ? num(hit.upgrade.pricePence) : null,
    };
}

/** Words for the in-chat card when the free questions are used up. */
export function quotaCardCopy(q, now) {
    const when = q && q.resetsAt ? shortDate(q.resetsAt, now) : '';
    if (q && q.code === 'bailey_staff_access') {
        return {
            eyebrow: 'Bailey',
            title: 'Bailey isn’t switched on for you',
            text: q.reason === 'no_plus' ? 'Bailey for the team comes with Bailey Plus. Ask the owner.' : 'Ask the owner to switch Bailey on for you in Team › Permissions.',
            cta: '',
        };
    }
    if (q && q.code === 'bailey_cap') {
        const cap = q.cap ? q.cap.toLocaleString('en-GB') : '';
        return {
            eyebrow: BAILEY_PLUS,
            title: `That’s this month’s ${cap ? `${cap} ` : ''}questions`,
            text: when ? `Bailey Plus resets on ${when}.` : 'Bailey Plus resets on the 1st.',
            cta: '',
        };
    }
    const allowance = (q && q.allowance) || FREE_BAILEY_QUESTIONS_FALLBACK;
    const price = q && q.upgradePricePence != null ? `${formatPrice(q.upgradePricePence)} a month` : '';
    return {
        eyebrow: 'Free questions',
        title: `That’s your ${allowance} free questions for this month`,
        text: when
            ? `I’ll be back to answer more on ${when}. Or get up to 1,000 a month with Bailey Plus.`
            : 'I’ll be back to answer more next month. Or get up to 1,000 a month with Bailey Plus.',
        cta: price ? `Get Bailey Plus · ${price}` : 'Get Bailey Plus',
    };
}

/** Month key for "dismissed this period" when nothing better is known. */
const monthKey = (now) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

/**
 * The light alerts, most urgent first. Each:
 *   { id, key, tone: 'bad'|'warn'|'info', title, text, cta }
 * `key` changes each period, so dismissing one hides it until the next.
 */
export function pricingAlerts(usage, now = new Date()) {
    if (!usage) return [];
    const out = [];
    const bal = usage.balance || {};
    const settle = bal.settlement || {};
    const period = (usage.bailey && usage.bailey.resetsAt) || (usage.reminders && usage.reminders.resetsAt) || monthKey(now);

    if (settle.failedAt) {
        const due = num(settle.duePence);
        out.push({
            id: 'settlement-failed',
            key: `settlement-failed:${settle.failedAt}`,
            tone: 'bad',
            title: 'Update your card',
            text: due ? `We couldn’t take ${formatPence(due)} for your messages. Reminders are still sending.` : 'We couldn’t take your last payment for messages. Reminders are still sending.',
            cta: 'Update your card',
            to: '/settings/billing',
        });
    }

    const pence = num(bal.pence);
    if (!settle.failedAt && pence != null && pence < 0) {
        const due = num(settle.duePence) != null ? num(settle.duePence) : Math.abs(pence);
        const when = shortDate(settle.nextAt, now);
        out.push({
            id: 'negative-balance',
            key: `negative-balance:${settle.nextAt || period}`,
            tone: 'info',
            title: 'Your message balance is below zero',
            text: `Reminders are still sending. We’ll settle ${formatPence(due)}${when ? ` on ${when}` : ' at the end of the month'}.`,
            cta: 'See usage',
        });
    }

    const rs = receptionistState(usage);
    const rec = usage.receptionist || {};
    const level = num(rec.alertLevel) || 0;
    if (rs.state === 'on' && rs.overage.capReached) {
        out.push({
            id: 'receptionist-cap',
            key: `receptionist-cap:${period}`,
            tone: 'warn',
            title: 'Your receptionist has reached your monthly limit',
            text: 'It’s taking messages until the 1st. Raise your limit to keep it answering.',
            cta: 'Change limit',
        });
    } else if (rs.state === 'on' && level >= 100) {
        out.push({
            id: 'receptionist-100',
            key: `receptionist-100:${period}`,
            tone: 'info',
            title: 'Your receptionist has used this month’s allowance',
            text: overageLine(usage) || 'It takes messages until the 1st.',
            cta: 'See usage',
        });
    } else if (rs.state === 'on' && level >= 80) {
        out.push({
            id: 'receptionist-80',
            key: `receptionist-80:${period}`,
            tone: 'info',
            title: 'Your receptionist has used most of this month’s allowance',
            text: overageLine(usage) || 'You can see its use in Plan & billing.',
            cta: 'See usage',
        });
    } else if (rs.state === 'trial' && rs.trial.endsAt) {
        const days = (new Date(rs.trial.endsAt) - now) / 864e5;
        if (days <= 3) {
            out.push({
                id: 'receptionist-trial',
                key: `receptionist-trial:${rs.trial.endsAt}`,
                tone: 'info',
                title: 'Your receptionist trial ends soon',
                text: `It stops on ${shortDate(rs.trial.endsAt, now)} unless you add it.`,
                cta: 'Keep it',
            });
        }
    }
    return out;
}

/** The top alert not yet dismissed, or null. */
export function nextAlert(usage, dismissed, now) {
    const set = dismissed instanceof Set ? dismissed : new Set(dismissed || []);
    return pricingAlerts(usage, now).find((a) => !set.has(a.key)) || null;
}

/** What an employed team member without Bailey reads (pricing 2026-10-07): who to ask, by name. */
export function staffLockedCopy(status) {
    const who = (status && status.ownerName) || 'the owner';
    const Who = who.charAt(0).toUpperCase() + who.slice(1);
    return {
        title: 'Bailey isn’t on for you yet',
        text: status && status.detail === 'no_plus'
            ? `Bailey for the team comes with Bailey Plus. ${Who === 'The owner' ? 'Ask the owner' : `Ask ${Who}`} if you’d like it.`
            : `Ask ${who} to switch it on for you in Team › Permissions.`,
        note: 'When it’s on, you can ask about your own diary, clients and earnings, and anything else your role lets you see.',
    };
}
